import { $prose } from '@milkdown/utils';
import { Plugin, PluginKey, TextSelection, type EditorState } from 'prosemirror-state';
import { Decoration, DecorationSet, type EditorView } from 'prosemirror-view';

type ProseMirrorNode = EditorState['doc'];

interface RootBlock {
  node: ProseMirrorNode;
  position: number;
  headingLevel: number | null;
  sectionEndIndex: number | null;
}

interface ToggleHeadingFold {
  position: number;
}

interface HeadingFoldingState {
  collapsedHeadings: Set<number>;
  suppressed: boolean;
}

interface SetHeadingFoldingSuppressed {
  suppressed: boolean;
}

const headingFoldingPluginKey = new PluginKey<HeadingFoldingState>('headingFolding');

export function createHeadingFoldingPlugin(
  initialCollapsedPositions: readonly number[],
  onCollapsedPositionsChange: (positions: number[]) => void
) {
  const pluginKey = headingFoldingPluginKey;

  return $prose(
    () =>
      new Plugin<HeadingFoldingState>({
        key: pluginKey,
        state: {
          init: (_, state) => {
            const foldablePositions = getFoldableHeadingPositions(state.doc);
            return {
              collapsedHeadings: new Set(initialCollapsedPositions.filter(position => foldablePositions.has(position))),
              suppressed: false
            };
          },
          apply(transaction, foldingState) {
            let nextCollapsedHeadings = foldingState.collapsedHeadings;

            if (transaction.docChanged) {
              const foldablePositions = getFoldableHeadingPositions(transaction.doc);
              nextCollapsedHeadings = new Set<number>();

              for (const position of foldingState.collapsedHeadings) {
                const mapped = transaction.mapping.mapResult(position + 1, -1);
                const resolved = transaction.doc.resolve(mapped.pos);
                if (!mapped.deletedAcross && resolved.parent.type.name === 'heading') {
                  const mappedHeadingPosition = resolved.before();
                  if (foldablePositions.has(mappedHeadingPosition)) {
                    nextCollapsedHeadings.add(mappedHeadingPosition);
                  }
                }
              }
            }

            const toggle = transaction.getMeta(pluginKey) as ToggleHeadingFold | undefined;
            if (toggle?.position != null) {
              nextCollapsedHeadings = new Set(nextCollapsedHeadings);
              if (nextCollapsedHeadings.has(toggle.position)) {
                nextCollapsedHeadings.delete(toggle.position);
              } else {
                nextCollapsedHeadings.add(toggle.position);
              }
            }

            const suppression = transaction.getMeta('headingFoldingSuppressed') as SetHeadingFoldingSuppressed | undefined;
            const nextSuppressed = suppression?.suppressed ?? foldingState.suppressed;
            if (nextCollapsedHeadings === foldingState.collapsedHeadings && nextSuppressed === foldingState.suppressed) {
              return foldingState;
            }

            return {
              collapsedHeadings: nextCollapsedHeadings,
              suppressed: nextSuppressed
            };
          }
        },
        props: {
          decorations(state) {
            return createDecorations(state, pluginKey);
          }
        },
        view(view) {
          reportCollapsedPositions(view.state, pluginKey, onCollapsedPositionsChange);
          return {
            update(view, previousState) {
              if (pluginKey.getState(view.state) !== pluginKey.getState(previousState)) {
                reportCollapsedPositions(view.state, pluginKey, onCollapsedPositionsChange);
              }
            }
          };
        }
      })
  );
}

function reportCollapsedPositions(
  state: EditorState,
  pluginKey: PluginKey<HeadingFoldingState>,
  onChange: (positions: number[]) => void
): void {
  onChange([...(pluginKey.getState(state)?.collapsedHeadings ?? [])].sort((left, right) => left - right));
}

function createDecorations(state: EditorState, pluginKey: PluginKey<HeadingFoldingState>): DecorationSet {
  const blocks = getRootBlocks(state.doc);
  const foldingState = pluginKey.getState(state);
  const collapsedHeadings = foldingState?.suppressed ? new Set<number>() : (foldingState?.collapsedHeadings ?? new Set<number>());
  const activeCollapsedLevels: number[] = [];
  const decorations: Decoration[] = [];

  blocks.forEach((block, index) => {
    const level = block.headingLevel;
    if (level != null) {
      while (activeCollapsedLevels.length > 0 && activeCollapsedLevels[activeCollapsedLevels.length - 1]! >= level) {
        activeCollapsedLevels.pop();
      }
    }

    if (activeCollapsedLevels.length > 0) {
      decorations.push(
        Decoration.node(block.position, block.position + block.node.nodeSize, { class: 'heading-fold-hidden' })
      );
    }

    if (level == null || block.sectionEndIndex == null || block.sectionEndIndex <= index + 1) {
      return;
    }

    const collapsed = collapsedHeadings.has(block.position);
    decorations.push(
      Decoration.node(block.position, block.position + block.node.nodeSize, { class: 'heading-fold-heading' }),
      Decoration.widget(block.position + 1, (view, getPosition) => createToggleButton(view, getPosition, pluginKey), {
        side: -1,
        ignoreSelection: true,
        stopEvent: () => true,
        key: `heading-fold-${block.position}-${collapsed}`
      })
    );

    if (collapsed) {
      activeCollapsedLevels.push(level);
    }
  });

  return DecorationSet.create(state.doc, decorations);
}

function createToggleButton(
  view: EditorView,
  getPosition: () => number | undefined,
  pluginKey: PluginKey<HeadingFoldingState>
): HTMLElement {
  const widgetPosition = getPosition();
  const headingPosition = widgetPosition == null ? null : widgetPosition - 1;
  const collapsed = headingPosition == null
    ? false
    : (pluginKey.getState(view.state)?.collapsedHeadings.has(headingPosition) ?? false);
  const action = collapsed ? 'Expand section' : 'Collapse section';
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'heading-fold-toggle';
  button.contentEditable = 'false';
  button.title = action;
  button.setAttribute('aria-label', action);
  button.setAttribute('aria-expanded', String(!collapsed));
  button.addEventListener('mousedown', event => event.preventDefault());
  button.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();

    const currentWidgetPosition = getPosition();
    if (currentWidgetPosition != null) {
      toggleHeading(view, currentWidgetPosition - 1, pluginKey);
    }
  });

  return button;
}

function toggleHeading(view: EditorView, headingPosition: number, pluginKey: PluginKey<HeadingFoldingState>): void {
  const blocks = getRootBlocks(view.state.doc);
  const headingIndex = blocks.findIndex(block => block.position === headingPosition);
  const heading = blocks[headingIndex];
  if (heading == null || heading.headingLevel == null || heading.sectionEndIndex == null) {
    return;
  }

  const transaction = view.state.tr;
  const collapsed = pluginKey.getState(view.state)?.collapsedHeadings.has(headingPosition) ?? false;
  if (!collapsed) {
    const sectionStart = heading.position + heading.node.nodeSize;
    const sectionEnd = blocks[heading.sectionEndIndex]?.position ?? view.state.doc.content.size;
    if (view.state.selection.to > sectionStart && view.state.selection.from < sectionEnd) {
      transaction.setSelection(TextSelection.create(transaction.doc, heading.position + heading.node.nodeSize - 1));
    }
  }

  view.dispatch(transaction.setMeta(pluginKey, { position: headingPosition } satisfies ToggleHeadingFold));
}

export function setHeadingFoldingSuppressed(view: EditorView, suppressed: boolean): void {
  if (headingFoldingPluginKey.getState(view.state)?.suppressed === suppressed) {
    return;
  }

  view.dispatch(view.state.tr.setMeta('headingFoldingSuppressed', { suppressed } satisfies SetHeadingFoldingSuppressed));
}

function getFoldableHeadingPositions(doc: ProseMirrorNode): Set<number> {
  const blocks = getRootBlocks(doc);
  return new Set(
    blocks
      .filter((block, index) => block.headingLevel != null && block.sectionEndIndex != null && block.sectionEndIndex > index + 1)
      .map(block => block.position)
  );
}

function getRootBlocks(doc: ProseMirrorNode): RootBlock[] {
  const blocks: RootBlock[] = [];
  const openHeadingIndexes: number[] = [];

  doc.forEach((node, position) => {
    const headingLevel = node.type.name === 'heading' ? (node.attrs.level as number) : null;
    const index = blocks.length;

    if (headingLevel != null) {
      while (
        openHeadingIndexes.length > 0 &&
        blocks[openHeadingIndexes[openHeadingIndexes.length - 1]!]!.headingLevel! >= headingLevel
      ) {
        blocks[openHeadingIndexes.pop()!]!.sectionEndIndex = index;
      }

      openHeadingIndexes.push(index);
    }

    blocks.push({ node, position, headingLevel, sectionEndIndex: null });
  });

  for (const index of openHeadingIndexes) {
    blocks[index]!.sectionEndIndex = blocks.length;
  }

  return blocks;
}
